<template>
	<div class="profile-summary">
		<div class="profile-summary__avatar">
			<slot name="avatar">
				<head-image
					:name="avatarName"
					:url="avatarUrl"
					:size="avatarSize"
					:online="online"
					:radius="avatarRadius"
				></head-image>
			</slot>
		</div>
		<div class="profile-summary__body">
			<div class="profile-summary__title-row">
				<div class="profile-summary__title-wrap">
					<div class="profile-summary__title" :title="titleTitle || title">{{ title }}</div>
					<slot name="title-extra"></slot>
				</div>
				<slot name="header-action"></slot>
			</div>
			<div
				v-if="subtitle"
				class="profile-summary__subtitle"
				:title="subtitleTitle || subtitle"
			>{{ subtitle }}</div>
			<div v-if="metaItems.length" class="profile-summary__meta">
				<div
					v-for="(item, index) in metaItems"
					:key="`${index}-${item.text}`"
					class="profile-summary__chip"
					:class="item.className"
					:title="item.title || item.text"
				>
					<i v-if="item.icon" :class="item.icon"></i>
					<span class="profile-summary__chip-text">{{ item.text }}</span>
				</div>
			</div>
			<div v-if="$slots.extra" class="profile-summary__extra">
				<slot name="extra"></slot>
			</div>
		</div>
	</div>
</template>

<script>
import HeadImage from './HeadImage.vue';

export default {
	name: 'ProfileSummary',
	components: {
		HeadImage
	},
	props: {
		avatarName: {
			type: String,
			default: ''
		},
		avatarRadius: {
			type: String,
			default: '50%'
		},
		avatarSize: {
			type: Number,
			default: 58
		},
		avatarUrl: {
			type: String,
			default: ''
		},
		metaItems: {
			type: Array,
			default: () => []
		},
		online: {
			type: Boolean,
			default: false
		},
		subtitle: {
			type: String,
			default: ''
		},
		subtitleTitle: {
			type: String,
			default: ''
		},
		title: {
			type: String,
			default: ''
		},
		titleTitle: {
			type: String,
			default: ''
		}
	}
};
</script>

<style scoped lang="scss">
.profile-summary {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	min-width: 0;

	&__avatar {
		flex-shrink: 0;
	}

	&__body {
		flex: 1;
		min-width: 0;
	}

	&__title-row {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 8px;
		min-width: 0;
	}

	&__title-wrap {
		display: flex;
		align-items: center;
		gap: 5px;
		min-width: 0;
	}

	&__title {
		min-width: 0;
		font-size: 18px;
		line-height: 1.25;
		font-weight: 600;
		color: var(--im-text-color);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__subtitle {
		margin-top: 4px;
		font-size: 12px;
		line-height: 1.4;
		color: var(--im-text-color-light);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__meta {
		margin-top: 8px;
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	&__chip {
		max-width: 100%;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		padding: 3px 8px;
		border-radius: 999px;
		background: var(--im-control-soft-bg);
		color: var(--im-text-color-light);
		font-size: 11px;
		line-height: 1.3;
		border: 1px solid var(--im-control-border);

		&.success {
			color: var(--im-color-success);
			background: rgba(var(--im-success-rgb, 103, 194, 58), 0.08);
			border-color: rgba(var(--im-success-rgb, 103, 194, 58), 0.18);
		}

		&.danger {
			color: var(--im-color-danger);
			background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.08);
			border-color: rgba(var(--im-danger-rgb, 245, 108, 108), 0.18);
		}

		i {
			font-size: 12px;
		}
	}

	&__chip-text {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__extra {
		margin-top: 10px;
		min-width: 0;
	}
}
</style>
